import React, { useMemo } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import ProprietyLogo from '../common/ProprietyLogo';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';
import { useUser } from './UserContext';
import { authService } from '../services/authService';
import { getSigninURLWithUTM } from '../utils/urlUtils';
import { usePersistedDataStore, useLocalDataStore } from './store';
import CircularProgress from '@mui/material/CircularProgress';
import Haverstraw from '../images/Haverstraw.png';
import axiosInstance from '../fetchConfig/axiosConfig.js'
import {toast} from '../common/toast'
import { useNYData } from '../common/NyDataObject.js';
import logger from '../utils/logger';
import { resolveCourtDateForContext } from '../utils/offerContextUtils.js';
import { useUnsentOffersNavigationGuard } from './CaseReview/SummaryCaseTable/hooks/useUnsentOffersNavigationGuard.js';
import UnsentOffersLeaveDialog from './CaseReview/SummaryCaseTable/components/UnsentOffersLeaveDialog.js';

const formatCourtDateLabel = (courtDate) => {
  if (!courtDate || courtDate === 'All') return null;
  const [year, month, day] = String(courtDate).split('-').map((num) => parseInt(num, 10));
  if (!year || !month || !day) return null;
  const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  });
};

function ResponsiveAppBar() {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const { user, logout, loading, canUseFeature, townView } = useUser();
  const navigate = useNavigate();
  // const {logoutAndNavigate} = useAuth();
  const getIsFetching = usePersistedDataStore((state) => state.isFetching);
  const location = useLocation(); // Get the current location
  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const getReportType = usePersistedDataStore((state) => state.reportType);
  const getCompSheet = usePersistedDataStore((state) => state.compSheet);
  const getReviewPage = useLocalDataStore((state) => state.reviewPage);
  const setTimeAdjustmentSettings = usePersistedDataStore((state) => state.setTimeAdjustmentSettings);
  const setIsFetching = usePersistedDataStore((state) => state.setIsFetching);
  const setReviewPage = useLocalDataStore((state) => state.setReviewPage);
  const setNegotiationObj = usePersistedDataStore((state) => state.setNegotiationObj);
  const { getLocationNameFromMuniCode } = useNYData();
  const {
    dialogOpen: unsentOffersDialogOpen,
    guardNavigation,
    confirmLeave: confirmUnsentOffersLeave,
    cancelLeave: cancelUnsentOffersLeave,
    shouldGuard: shouldGuardUnsentOffers,
  } = useUnsentOffersNavigationGuard();
  // abstract this into a higher level thing on load (like auth context)
  const darkMode = false;

  // const restrictedEmails = ['
  // const isAdmin = user?.userAccess?.level === 'admin' || user?.userAccess?.level === 'propadmin';
  // const isRestricted = !isAdmin && user?.userAccess?.level !== 'aventine';

  // Specialized report modes (back button, hide nav) — distinct from standard /analysis runs
  const isReportView = getNegotiationObj?.reportType &&
    (getNegotiationObj.reportType === 'data_analysis' ||
      getNegotiationObj.reportType === 'settlement' ||
      getNegotiationObj.reportType === 'marketing' ||
      getCompSheet?.length > 0);

  // /analysis = muni selection + Run (no header). /analysis/:reviewid = summary or case review session.
  const isAnalysisSessionRoute = /^\/analysis\/[^/]+/.test(location.pathname);
  const showCenterHeader =
    (townView && user?.isLinkAuthenticated) || isReportView || isAnalysisSessionRoute || getIsFetching;

  const headerInfo = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    const isTownLink = townView && user?.isLinkAuthenticated;

    const muniCode = String(
      isTownLink
        ? (getNegotiationObj?.MuniCode || user?.muni_code || '')
        : (getNegotiationObj?.MuniCode || queryParams.get('MuniCode') || ''),
    );
    const resolved = getLocationNameFromMuniCode(muniCode);
    const locationName =
      resolved && resolved !== muniCode.toUpperCase() ? resolved : '';
    const placeLabel =
      muniCode.length === 1 ? 'County' : muniCode.length > 3 ? 'Village' : 'Town';

    if (isTownLink) {
      const courtDate = resolveCourtDateForContext(getNegotiationObj, user);
      return {
        isTownLink: true,
        title: `${user?.client_name || 'Aventine'} Cases`,
        locationName,
        muniCode,
        taxYear: getNegotiationObj?.TaxYear || user?.tax_year,
        courtDate,
        formattedCourtDate: formatCourtDateLabel(courtDate),
      };
    }

    const courtDate =
      getNegotiationObj?.CourtDate || decodeURIComponent(queryParams.get('CourtDate') || '');
    const taxYear = queryParams.get('TaxYear') || getNegotiationObj?.TaxYear;
    const reportType =
      getNegotiationObj?.reportType || queryParams.get('reportType') || getReportType || 'standard';
    const runType = queryParams.get('runType') || 'all_clients';

    const formattedCourtDate = formatCourtDateLabel(courtDate);

    let title = 'Sales Analysis';
    switch (reportType) {
      case 'data_analysis':
        title = 'Data Cleaning Report';
        break;
      case 'settlement':
        title = 'Settlement Analysis';
        break;
      case 'marketing':
        title = 'Marketing Analysis';
        break;
      case 'assessment':
        title = 'Assessment Analysis';
        break;
      default:
        switch (runType) {
          case 'court_date':
            title = 'Court Date Analysis';
            break;
          case 'single_parcel':
            title = 'Single Parcel Analysis';
            break;
          case 'representative':
            title = 'Representative Analysis';
            break;
          default:
            break;
        }
    }

    return {
      isTownLink: false,
      title,
      locationName,
      placeLabel,
      muniCode,
      taxYear,
      courtDate: courtDate || null,
      formattedCourtDate,
    };
  }, [
    townView,
    user,
    getNegotiationObj,
    getReportType,
    location.search,
    getLocationNameFromMuniCode,
  ]);

  const displayInfo = () => {
    if (!headerInfo) return null;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
          {headerInfo.title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
          {headerInfo.locationName && (
            <>
              <span>{headerInfo.locationName}</span>
              {!headerInfo.isTownLink && headerInfo.muniCode ? (
                <span className="mx-1">({headerInfo.muniCode})</span>
              ) : null}
              <span className="mx-1">•</span>
            </>
          )}
          <span>Tax Year: {headerInfo.taxYear}</span>
          {headerInfo.formattedCourtDate ? (
            <>
              <span className="mx-1">•</span>
              <span>Court Date: {headerInfo.formattedCourtDate}</span>
            </>
          ) : null}
        </Typography>
      </Box>
    );
  };

  const navigateAwayFromSummaryToAnalysis = (queryParams) => {
    setIsFetching(false);
    setReviewPage(true);
    setNegotiationObj(null);
    navigate(`/analysis?${queryParams.toString()}`);
  };

  const handleBackClick = () => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.delete('comp');
    setTimeAdjustmentSettings(null);
    setIsFetching(false);
    
    if (townView) {
      // For town view, always go back to the client route
      const clientName = user?.client_name || 'Aventine';
      setReviewPage(true);
      setNegotiationObj(null);
      navigate(`/${clientName}?${queryParams.toString()}`);
    } else {
      // If we're in case review (reviewPage false), go back to summary table
      if (!getReviewPage) {
        setReviewPage(true);
        navigate(`/analysis/1?${queryParams.toString()}`);
      } else {
        guardNavigation(() => navigateAwayFromSummaryToAnalysis(queryParams));
      }
    }
  };

  const handleSummaryTableClick = () => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.delete('comp');
    setTimeAdjustmentSettings(null);
    setIsFetching(false);
    
    if (townView) {
      // For town view, always go back to the client route
      const clientName = user?.client_name || 'Aventine';
      setReviewPage(true);
      setNegotiationObj(null);
      navigate(`/${clientName}?${queryParams.toString()}`);
    } else {
      // If we're in summary table (reviewPage true), go back to analysis
      if (getReviewPage) {
        guardNavigation(() => navigateAwayFromSummaryToAnalysis(queryParams));
      } else {
        // Otherwise go to summary table (but not for data_analysis)
        if (getNegotiationObj?.reportType === 'data_analysis') {
          // For data analysis, always go back to analysis
          setReviewPage(true);
          setNegotiationObj(null);
          navigate(`/analysis?${queryParams.toString()}`);
        } else {
          // For other report types, go to summary table
          setReviewPage(true);
          navigate(`/analysis/1?${queryParams.toString()}`);
        }
      }
    }
  };

  const handleNavPageClick = (event, page, route) => {
    setIsFetching(false);
    if (page !== 'Analysis' || !shouldGuardUnsentOffers()) {
      return;
    }
    event.preventDefault();
    const queryParams = new URLSearchParams(location.search);
    queryParams.delete('comp');
    setTimeAdjustmentSettings(null);
    guardNavigation(() => navigateAwayFromSummaryToAnalysis(queryParams));
  };

  async function handleClearCache() {
    try {
      const response = await axiosInstance.get('/clear_cache')
      logger.log(response)
      if(response.data && response.data.message){
        // Clear localStorage
        localStorage.clear()
        toast.success('Cache and localStorage cleared')
        // Force refresh after clearing
        window.location.reload()
      }
    } catch (error) {
      logger.error('Error clearing cache:', error)
    }
  }

  // Define pages based on view type
  const isReportsUser = user?.userAccess?.level === 'admin' || user?.userAccess?.level === 'propadmin';

  const pages = user
    ? townView
      ? [
          { page: 'Cases', route: `/${user?.client_name || 'Aventine'}`, isLoading: false, end: true },
        ]
      : [
          { page: 'Analysis', route: '/analysis', isLoading: getIsFetching },
          { page: 'Graphs', route: '/graph', disabled: !canUseFeature('visualization_tool') },
          ...(isReportsUser ? [{ page: 'Reports', route: '/reports' }] : []),
        ]
    : [{ page: 'Home', route: '/' }];

  const userSettings = [
    ...(!user?.isLinkAuthenticated
      ? [location.pathname === '/snap'
          ? {
              label: 'Leave Snap mode',
              action: () => guardNavigation(() => navigate('/analysis')),
            }
          : {
              label: 'Swap to Snap mode',
              action: () => guardNavigation(() => navigate('/snap')),
            }]
      : []),
    {
      label: 'Profile',
      action: () => logger.log('/profile'),
      disabled: true,
    },
    {
      label: 'Clear Cache',
      action: handleClearCache,
    },
    {
      label: 'Logout',
      action: async () => {
        await authService.logout();
        // Preserve UTM parameters when navigating to signin
        navigate(getSigninURLWithUTM());
      },
    },
  ];

  const isTownViewLinkMenu = Boolean(townView && user?.isLinkAuthenticated);
  const userSettingsMenuItems = isTownViewLinkMenu
    ? userSettings.filter(
        (s) => s.label !== 'Profile' && s.label !== 'Clear Cache'
      )
    : userSettings;

  const showReportBackNav = isReportView && !townView;
  const showNavPages = !isReportView || townView;
  const clientRoute = `/${user?.client_name || 'Aventine'}`;

  const summaryTableButtonLabel =
    getNegotiationObj?.reportType === 'data_analysis'
      ? 'Analysis'
      : getReviewPage
        ? 'Analysis'
        : 'Summary Table';

  const userDisplayName = isTownViewLinkMenu
    ? (headerInfo?.locationName ? `${headerInfo.locationName} Assessor` : 'Town View')
    : user?.userName;

  const userSubtitle = isTownViewLinkMenu ? 'Secure Link Access' : user?.organization;

  const townViewAvatarLetter = headerInfo?.locationName?.trim()
    ? headerInfo.locationName.trim().charAt(0).toUpperCase()
    : '?';

  const getNavLinkActiveSx = (page) => {
    if (townView && user?.isLinkAuthenticated) {
      const isActive =
        location.pathname === clientRoute ||
        (page === 'Cases' && location.pathname === '/analysis');
      return isActive ? 'primary.main' : 'black';
    }
    // Default inactive color; NavLink's .active class promotes the current tab to primary.main
    return 'black';
  };

  const handleLogoClick = () => {
    setIsFetching(false);
    if (townView) {
      navigate(clientRoute);
    }
  };

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const leftToolbarSection = showReportBackNav ? (
    <div className="flex items-center">
      <IconButton onClick={handleBackClick} sx={{ mr: 0.5 }}>
        <ArrowBackIcon />
      </IconButton>
      <Button
        onClick={handleSummaryTableClick}
        sx={{
          textTransform: 'none',
          color: 'text.primary',
          padding: '4px 8px',
          whiteSpace: 'nowrap',
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'primary.main',
          },
        }}
      >
        {summaryTableButtonLabel}
      </Button>
    </div>
  ) : (
    <div
      onClick={handleLogoClick}
      style={{ cursor: townView ? 'pointer' : 'default' }}
    >
      <ProprietyLogo style={{ height: '100%' }} />
    </div>
  );

  const navPagesSection = showNavPages && (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        ml: 2,
      }}
    >
      {pages.map(({ page, route, disabled, end, isLoading }) => (
        <Button
          key={page}
          component={NavLink}
          to={`${route}${location.search}`}
          end={end}
          disabled={disabled}
          disableRipple
          onClick={(event) => handleNavPageClick(event, page, route)}
          endIcon={
            <Box
              sx={{
                width: 12,
                height: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ml: '0 !important',
              }}
            >
              {isLoading && <CircularProgress size={12} />}
            </Box>
          }
          sx={{
            ml: 1,
            textDecoration: 'none',
            color: getNavLinkActiveSx(page),
            '&.active': { color: 'primary.main' },
            '&:hover': { backgroundColor: 'transparent', color: 'primary.main' },
            padding: '6px 6px',
          }}
        >
          {page}
        </Button>
      ))}
    </Box>
  );

  const signedInToolbarSection = user && (
    <Box className="flex items-center gap-2 justify-end min-w-0 md:w-[200px]">
      <Box className="min-w-0 max-w-[28vw] sm:max-w-[40vw] md:max-w-[140px]">
        <Typography
          noWrap
          sx={{
            fontWeight: 'bold',
            fontSize: '0.85rem',
            textAlign: 'right',
          }}
        >
          {userDisplayName}
        </Typography>
        <Typography
          noWrap
          sx={{
            fontSize: '0.625rem',
            color: 'gray',
            textAlign: 'right',
          }}
        >
          {userSubtitle}
        </Typography>
      </Box>
      <Tooltip title="Open settings">
        <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
          {isTownViewLinkMenu ? (
            <Avatar sx={{ bgcolor: 'grey.600', color: 'grey.100' }}>{townViewAvatarLetter}</Avatar>
          ) : (
            <Avatar alt={user.userName} src={Haverstraw} />
          )}
        </IconButton>
      </Tooltip>
      <Menu
        sx={{ mt: '45px' }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        {userSettingsMenuItems.map((setting) => (
          <MenuItem
            key={setting.label}
            onClick={() => {
              setting.action();
              handleCloseUserMenu();
            }}
            disabled={setting.disabled}
          >
            <Typography sx={{ textAlign: 'center' }}>{setting.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );

  const signedOutToolbarSection = loading ? (
    <CircularProgress size={20} />
  ) : (
    <Tooltip title="Sign In">
      <Button component={NavLink} to="/signin" variant="contained">
        Sign In
      </Button>
    </Tooltip>
  );

  const toolbarUserSection = user ? signedInToolbarSection : signedOutToolbarSection;

  return (
    <>
    <UnsentOffersLeaveDialog
      open={unsentOffersDialogOpen}
      onStay={cancelUnsentOffersLeave}
      onLeaveAnyway={confirmUnsentOffersLeave}
    />
    <AppBar
      position="static"
      sx={{
        backgroundColor: darkMode ? '#333' : 'white',
        boxShadow: 3,
        color: darkMode ? 'white' : 'black',
      }}
    >
      <Container maxWidth={false} className="z-[999]">
        <Toolbar
          disableGutters
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            position: 'relative',
            gap: 0.5,
            px: { xs: 0.5, md: 0 },
          }}
        >
          {showNavPages && (
            <Box className="flex md:hidden">
              <IconButton aria-label="Open navigation menu" color="inherit" onClick={handleOpenNavMenu}>
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorElNav}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {pages.map(({ page, route, disabled, end, isLoading }) => (
                  <MenuItem
                    key={page}
                    component={NavLink}
                    to={`${route}${location.search}`}
                    end={end}
                    disabled={disabled}
                    onClick={(event) => {
                      if (disabled) {
                        event.preventDefault();
                        return;
                      }
                      handleNavPageClick(event, page, route);
                      handleCloseNavMenu();
                    }}
                  >
                    <Typography sx={{ flexGrow: 1 }}>{page}</Typography>
                    {isLoading && <CircularProgress size={12} sx={{ ml: 1 }} />}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          )}

          <Box className="flex items-center h-full min-w-0 flex-1 overflow-hidden">
            <Box className={`${showReportBackNav ? '' : 'hidden'} md:block min-w-0 md:w-[200px]`}>{leftToolbarSection}</Box>
            <Box className="hidden md:flex items-center h-full">{navPagesSection}</Box>
          </Box>

          {/* Center - Report Info or Town View Info */}
          {showCenterHeader && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: 'auto',
                minWidth: '200px',
                maxWidth: 'calc(100% - 400px)',
              }}
            >
              <Box sx={{ textAlign: 'center', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                {displayInfo()}
                {getIsFetching && <CircularProgress size={16} />}
              </Box>
            </Box>
          )}

          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {toolbarUserSection}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
    </>
  );
}


export default React.memo(ResponsiveAppBar);
